import { shallowRef } from 'vue'
import { chargeLessonsTo } from '@/lib/savedCards'

/**
 * The payment waiting on the family's card, shown by LessonPaymentDialog:
 * `{ amount, message, subscriptionId, settle }`, or null.
 */
export const pendingLessonPayment = shallowRef(null)

/**
 * Send a lesson request or answer that may need paying for first.
 *
 * A lesson inside its last 48 hours is paid for when it is agreed, so the
 * server answers 409 until the family has confirmed the payment with their
 * bank. They choose a card, the price is held on it, and the request is sent
 * again with the hold; it is taken when the lesson is agreed.
 *
 * @param {number} subscriptionId
 * @param {(paymentIntentId: string|null) => Promise<unknown>} send
 */
export async function sendPayingIfNeeded(subscriptionId, send) {
  let needed

  try {
    return await send(null)
  } catch (failure) {
    if (failure?.response?.status !== 409 || !failure.response.data?.payment_needed) {
      throw failure
    }

    needed = failure.response.data
  }

  const held = await new Promise(settle => {
    pendingLessonPayment.value = { amount: Number(needed.amount), message: needed.message, subscriptionId, settle }
  })

  pendingLessonPayment.value = null

  if (!held) {
    throw refusal('Nothing was sent.')
  }

  let sent

  try {
    sent = await send(held.paymentIntentId)
  } catch (failure) {
    // The request did not go through, so nothing will take or release the
    // hold: give it back now rather than leave the money reserved.
    window.axios.delete(`/lesson-changes/arrangements/${subscriptionId}/payment-hold/${held.paymentIntentId}`).catch(() => {})
    throw failure
  }

  if (held.remember) {
    await chargeLessonsTo(held.paymentMethodId).catch(() => {})
  }

  return sent
}

function refusal(message) {
  return { response: { data: { message } } }
}
